import { EditorData } from "../../data/EditorData";
import { MouseEventUtil } from "../../utils/MouseEventUtil";
import { EventType } from "../../data/enums/EventType";
import { LabelType } from "../../data/enums/LabelType";
import { GeneralSelector } from "../../store/selectors/GeneralSelector";
import { RenderEngineSettings } from "../../settings/RenderEngineSettings";
import { LabelName } from "../../store/labels/types";
import { LabelsSelector } from "../../store/selectors/LabelsSelector";
import { EditorModel } from '../../staticModels/EditorModel';
import { PolygonRenderEngine } from './PolygonRenderEngine';
import { LineRenderEngine } from './LineRenderEngine';
import { EditorActions } from "../actions/EditorActions";

export type AnnotationClipboard = {
  labelType: LabelType;
  data: unknown;
};

export abstract class BaseRenderEngine {
  protected readonly canvas: HTMLCanvasElement;
  public labelType: LabelType;
  protected imageDataCache: any;
  private static readonly CLIPBOARD_KEY = "annotationClipboard";
  private static readonly LEGACY_CLIPBOARD_KEY = "copy";

  protected constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
  }

  public update(data: EditorData): void {
    if (!!data.event) {
      switch (MouseEventUtil.getEventType(data.event)) {
        case EventType.MOUSE_MOVE:
          this.mouseMoveHandler(data);
          break;
        case EventType.MOUSE_UP:
          this.mouseUpHandler(data);
          break;
        case EventType.MOUSE_DOWN:
          data.event.stopPropagation()
          this.mouseDownHandler(data);
          break;
        default:
          break;
      }
    }
  }

  protected abstract mouseDownHandler(data: EditorData): void;
  protected abstract mouseMoveHandler(data: EditorData): void;
  protected abstract mouseUpHandler(data: EditorData): void;
  public abstract pasteHandler(): void;

  public static getClipboard(): AnnotationClipboard | null {
    try {
      const stored = localStorage.getItem(BaseRenderEngine.CLIPBOARD_KEY);
      if (!stored) {
        return BaseRenderEngine.getLegacyClipboard();
      }

      const parsed = JSON.parse(stored);
      if (parsed?.labelType && parsed?.data !== undefined) {
        return parsed as AnnotationClipboard;
      }
    } catch {
      return null;
    }

    return null;
  }

  public static setClipboard(labelType: LabelType, data: unknown): void {
    const clipboard: AnnotationClipboard = { labelType, data };
    localStorage.setItem(
      BaseRenderEngine.CLIPBOARD_KEY,
      JSON.stringify(clipboard)
    );
    localStorage.setItem(BaseRenderEngine.LEGACY_CLIPBOARD_KEY, JSON.stringify(data));
  }

  public static canPasteInto(activeLabelType: LabelType): boolean {
    const clipboard = BaseRenderEngine.getClipboard();
    return !!clipboard && clipboard.labelType === activeLabelType;
  }

  private static getLegacyClipboard(): AnnotationClipboard | null {
    try {
      const stored = localStorage.getItem(BaseRenderEngine.LEGACY_CLIPBOARD_KEY);
      if (!stored) {
        return null;
      }

      const parsed = JSON.parse(stored);
      const data = Array.isArray(parsed) ? parsed[0] : parsed;
      if (!data) {
        return null;
      }

      const labelType = BaseRenderEngine.inferLabelTypeFromData(data);
      if (!labelType) {
        return null;
      }

      return { labelType, data };
    } catch {
      return null;
    }
  }

  private static inferLabelTypeFromData(data: Record<string, unknown>): LabelType | null {
    if (!data) {
      return null;
    }
    if ("rect" in data) {
      return LabelType.RECT;
    }
    if ("point" in data) {
      return LabelType.POINT;
    }
    if ("line" in data) {
      return LabelType.LINE;
    }
    return null;
  }

  public copyHandler() {
    if (!EditorModel.supportRenderingEngine) {
      return;
    }

    switch (EditorModel.supportRenderingEngine.labelType) {
      case LabelType.POLYGON:
        (EditorModel.supportRenderingEngine as PolygonRenderEngine).cancelLabelCreation();
        break;
      case LabelType.LINE:
        (EditorModel.supportRenderingEngine as LineRenderEngine).cancelLabelCreation();
        break;
    }

    const labelType = LabelsSelector.getActiveLabelType();
    const selectorSuffix =
      labelType.charAt(0) + labelType.slice(1).toLowerCase();
    const activeLabel = LabelsSelector[`getActive${selectorSuffix}Label`]();

    if (!activeLabel) {
      return;
    }

    this.imageDataCache = [activeLabel];
    BaseRenderEngine.setClipboard(labelType, activeLabel);
    EditorActions.fullRender();
  }

  protected getCopiedLabels<T>(): T[] {
    const clipboard = BaseRenderEngine.getClipboard();
    if (clipboard && clipboard.labelType === this.labelType && clipboard.data) {
      const copied = Array.isArray(clipboard.data)
        ? (clipboard.data as T[])
        : [clipboard.data as T];
      this.imageDataCache = copied;
      return copied;
    }

    if (Array.isArray(this.imageDataCache) && this.imageDataCache.length > 0) {
      return this.imageDataCache as T[];
    }

    return [];
  }

  abstract render(data: EditorData): void;

  abstract isInProgress(): boolean;

  protected static resolveLabelLineName(labelId: string): string {
    const labelName: LabelName | null =
      LabelsSelector.getLabelNameById(labelId);
    return labelName ? labelName.name : "Select Label";
  }

  protected static resolveLabelLineColor(
    labelId: string,
    isActive: boolean
  ): string {
    const perClassColor: boolean =
      GeneralSelector.getEnablePerClassColorationStatus();
    if (perClassColor) {
      const labelName: LabelName | null =
        LabelsSelector.getLabelNameById(labelId);
      return labelName
        ? labelName.color
        : RenderEngineSettings.DEFAULT_LINE_COLOR;
    } else {
      return isActive
        ? RenderEngineSettings.ACTIVE_LINE_COLOR
        : RenderEngineSettings.INACTIVE_LINE_COLOR;
    }
  }

  protected static resolveLabelAnchorColor(isActive: boolean): string {
    const perClassColor: boolean =
      GeneralSelector.getEnablePerClassColorationStatus();
    if (perClassColor) {
      return RenderEngineSettings.DEFAULT_ANCHOR_COLOR;
    } else {
      return isActive
        ? RenderEngineSettings.ACTIVE_ANCHOR_COLOR
        : RenderEngineSettings.INACTIVE_ANCHOR_COLOR;
    }
  }
}
